"use client"

import { ComponentProps, createContext, use } from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "cn"

import IconCross from "@/components/icons/cross"
import { Button } from "@/components/ui/button"
import { IconButton, type IconButtonProps } from "@/components/ui/icon-button"
import { useDialogAutoFocus } from "./dialog-presentation"

const INPUT_GROUP_CONTROL_SIZES = {
  sm: "text-sm leading-4 px-3",
  md: "text-base leading-5 px-3",
  lg: "text-md leading-5 px-4",
} as const

const INPUT_GROUP_HEIGHTS = {
  sm: "h-(--input-sm-size)",
  md: "h-(--input-md-size)",
  lg: "h-(--input-lg-size)",
} as const satisfies Record<keyof typeof INPUT_GROUP_CONTROL_SIZES, string>

export type InputGroupSize = keyof typeof INPUT_GROUP_CONTROL_SIZES

const INPUT_GROUP_ADDON_SIZES = {
  sm: "text-sm",
  md: "text-base",
  lg: "text-md",
} as const satisfies Record<InputGroupSize, string>

const INPUT_GROUP_INSET_VARS = {
  sm: "[--input-group-leading-inset:calc((var(--input-sm-size)-1.25em)/2)] [--input-group-button-inset:calc((var(--input-sm-size)-var(--button-xs-size))/2)] [--input-group-clear-inset:calc((var(--input-sm-size)-var(--button-xs-size))/4)] [--input-group-addon-gap:calc(var(--button-xs-size)/5)]",
  md: "[--input-group-leading-inset:calc((var(--input-md-size)-1.25em)/2)] [--input-group-button-inset:calc((var(--input-md-size)-var(--button-sm-size))/2)] [--input-group-clear-inset:calc((var(--input-md-size)-var(--button-xs-size))/4)] [--input-group-addon-gap:calc(var(--button-sm-size)/5)]",
  lg: "[--input-group-leading-inset:calc((var(--input-lg-size)-1.25em)/2)] [--input-group-button-inset:calc((var(--input-lg-size)-var(--button-md-size))/2)] [--input-group-clear-inset:calc((var(--input-lg-size)-var(--button-xs-size))/4)] [--input-group-addon-gap:calc(var(--button-md-size)/5)]",
} as const satisfies Record<InputGroupSize, string>

type ButtonSize = NonNullable<ComponentProps<typeof Button>["size"]>

const BUTTON_SIZE_BY_GROUP = {
  sm: "xs",
  md: "sm",
  lg: "md",
} as const satisfies Record<InputGroupSize, ButtonSize>

const InputGroupSizeContext = createContext<InputGroupSize>("md")

const inputGroupSizeVariants = {
  sm: cn(INPUT_GROUP_HEIGHTS.sm, INPUT_GROUP_INSET_VARS.sm),
  md: cn(INPUT_GROUP_HEIGHTS.md, INPUT_GROUP_INSET_VARS.md),
  lg: cn(INPUT_GROUP_HEIGHTS.lg, INPUT_GROUP_INSET_VARS.lg),
} as const satisfies Record<InputGroupSize, string>

const inputGroupVariants = cva(
  "group/input-group relative flex w-full min-w-0 items-center rounded-full border border-input bg-background transition-colors hover:border-gray-8 in-data-[slot=combobox-content]:focus-within:border-inherit has-[[data-slot=input-group-control]:disabled]:bg-input/50 has-[[data-slot=input-group-control]:disabled]:opacity-50 has-[[data-slot=input-group-control]:disabled]:hover:border-input has-[[data-slot=input-group-control]:focus-visible]:border-current has-[[data-slot=input-group-control]:read-only]:bg-foreground/5 has-[[data-slot][aria-invalid=true]]:border-destructive has-[>[data-align=inline-end]]:[&>input]:rounded-r-none has-[>[data-align=inline-end]]:[&>input]:pr-(--input-group-addon-gap) has-[>[data-align=inline-start]]:[&>input]:rounded-l-none has-[>[data-align=inline-start]]:[&>input]:pl-(--input-group-addon-gap)",
  {
    variants: {
      size: inputGroupSizeVariants,
    },
    defaultVariants: {
      size: "md",
    },
  }
)

const inputGroupInputVariants = cva(
  "peer flex h-full min-h-0 w-full min-w-0 flex-1 truncate rounded-[inherit] border-0 bg-transparent text-foreground transition-colors outline-none placeholder:text-muted focus:placeholder:text-gray-8 disabled:cursor-not-allowed disabled:bg-transparent",
  {
    variants: {
      size: INPUT_GROUP_CONTROL_SIZES,
    },
    defaultVariants: {
      size: "md",
    },
  }
)

export type InputGroupProps = ComponentProps<"div"> & {
  muted?: boolean
  size?: InputGroupSize
}

function InputGroup({
  className,
  muted,
  size = "md",
  ...props
}: InputGroupProps) {
  return (
    <InputGroupSizeContext value={size}>
      <div
        data-slot="input-group"
        data-size={size}
        role="group"
        className={cn(
          inputGroupVariants({ size }),
          muted &&
            "border-transparent bg-foreground/5 hover:border-transparent has-[[data-slot=input-group-control]:focus-visible]:bg-background",
          className
        )}
        {...props}
      />
    </InputGroupSizeContext>
  )
}

const inputGroupAddonVariants = cva(
  "flex h-auto cursor-text items-center justify-center gap-(--input-group-addon-gap) select-none group-data-[disabled=true]/input-group:opacity-50 *:[svg]:size-[1.25em]",
  {
    variants: {
      size: INPUT_GROUP_ADDON_SIZES,
      align: {
        "inline-start":
          "order-first pl-(--input-group-leading-inset) has-[button]:pl-(--input-group-button-inset)",
        "inline-end":
          "order-last pr-(--input-group-leading-inset) has-[button]:pr-(--input-group-button-inset) data-[slot=input-group-clear]:pr-(--input-group-clear-inset)",
      },
    },
    defaultVariants: {
      size: "md",
      align: "inline-start",
    },
  }
)

type InputGroupAddonProps = ComponentProps<"div"> & {
  align?: NonNullable<VariantProps<typeof inputGroupAddonVariants>["align"]>
}

function InputGroupAddon({
  className,
  align = "inline-start",
  ...props
}: InputGroupAddonProps) {
  const size = use(InputGroupSizeContext)

  return (
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      className={cn(inputGroupAddonVariants({ align, size }), className)}
      onClick={(e) => {
        if (e.target instanceof Element && e.target.closest("button")) return
        e.currentTarget.parentElement?.querySelector("input")?.focus()
      }}
      {...props}
    />
  )
}

type InputGroupButtonProps =
  | (Omit<IconButtonProps, "size"> & { size?: ButtonSize })
  | (Omit<ComponentProps<typeof Button>, "size"> & {
      icon?: never
      label?: never
      size?: ButtonSize
    })

function InputGroupButton(props: InputGroupButtonProps) {
  const groupSize = use(InputGroupSizeContext)
  const resolvedSize = props.size ?? BUTTON_SIZE_BY_GROUP[groupSize]

  if ("icon" in props && props.icon) {
    const {
      icon,
      label,
      type = "button",
      variant = "ghost",
      size: _size,
      ...rest
    } = props

    return (
      <IconButton
        data-slot="input-group-button"
        type={type}
        icon={icon}
        label={label}
        size={resolvedSize}
        variant={variant}
        {...rest}
      />
    )
  }

  const {
    type = "button",
    variant = "ghost",
    size: _size,
    className,
    ...rest
  } = props

  return (
    <Button
      data-slot="input-group-button"
      type={type}
      size={resolvedSize}
      variant={variant}
      className={cn("px-[.875em]", className)}
      {...rest}
    />
  )
}

function InputGroupText({ className, ...props }: ComponentProps<"span">) {
  const size = use(InputGroupSizeContext)

  return (
    <span
      className={cn("text-muted", INPUT_GROUP_ADDON_SIZES[size], className)}
      {...props}
    />
  )
}

export type InputGroupInputProps = Omit<ComponentProps<"input">, "size">

function InputGroupInput({
  type = "text",
  className,
  autoFocus,
  ref,
  ...props
}: InputGroupInputProps) {
  const size = use(InputGroupSizeContext)
  const autoFocusProps = useDialogAutoFocus(autoFocus, ref)

  return (
    <input
      type={type}
      data-slot="input-group-control"
      className={cn(inputGroupInputVariants({ size }), className)}
      {...autoFocusProps}
      {...props}
    />
  )
}

function hasInputGroupClearValue(
  value: string | number | readonly string[] | null | undefined
) {
  if (value == null) return false
  if (Array.isArray(value)) return value.length > 0
  return String(value) !== ""
}

export type InputGroupClearProps = {
  value?: string | number | readonly string[] | null
  onClear: () => void
} & Omit<ComponentProps<"button">, "onClick" | "type">

function InputGroupClear({
  value,
  onClear,
  className,
  ...props
}: InputGroupClearProps) {
  if (!hasInputGroupClearValue(value)) {
    return null
  }

  return (
    <InputGroupAddon align="inline-end" data-slot="input-group-clear">
      <button
        type="button"
        aria-label="Clear"
        className={cn(
          "group/clear flex size-(--button-xs-size) items-center justify-center",
          className
        )}
        onClick={(e) => {
          onClear()
          e.currentTarget
            .closest("[data-slot=input-group]")
            ?.querySelector<HTMLInputElement>("input")
            ?.focus()
        }}
        {...props}
      >
        <span className="flex size-4.5 items-center justify-center rounded-full text-2xs text-gray-8 transition-colors group-hover/clear:bg-gray-7 group-hover/clear:text-gray-1">
          <IconCross strokeWidth={2.5} />
        </span>
      </button>
    </InputGroupAddon>
  )
}

export {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupText,
  InputGroupInput,
  InputGroupClear,
}
